import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import { toast } from "sonner";

const YEARS = [2021, 2022, 2023];

const emptyYear = () => ({
  fatturato: "", costo_personale: "", dipendenti: "", persone_progetto: "",
  costo_amministratore: "", costi_professionisti: "",
});

const NumField = ({ label, help, testid, value, onChange, integer }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-[#2B3A42]">{label}</Label>
    <Input
      data-testid={testid}
      className="h-11 bg-white border-[#DDE5E2] focus-visible:ring-[#6C8EAD] tnum"
      inputMode={integer ? "numeric" : "decimal"}
      value={value}
      onChange={(e) => {
        const raw = e.target.value;
        const clean = integer ? raw.replace(/[^\d]/g, "") : raw.replace(/[^\d.,]/g, "");
        onChange(clean);
      }}
      placeholder={integer ? "0" : "0,00"}
    />
    {help && <p className="text-xs text-[#5B6E78]">{help}</p>}
  </div>
);

const toNum = (v) => {
  if (v === "" || v == null) return null;
  const n = parseFloat(String(v).replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? null : n;
};

const touched = (y) =>
  Object.values(y).some((v) => v !== "" && toNum(v) !== 0 && toNum(v) !== null);

export const YearWizard = ({ value, onBack, onConfirm, submitting }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(value || { 2021: emptyYear(), 2022: emptyYear(), 2023: emptyYear() });
  const year = YEARS[step];
  const yd = data[year];
  const up = (k, v) => setData((p) => ({ ...p, [year]: { ...p[year], [k]: v } }));

  const validateYear = (y, yv) => {
    if (!touched(yv)) return null; // empty year allowed
    const req = {
      fatturato: "Fatturato", costo_personale: "Costo del personale",
      dipendenti: "Numero dipendenti", persone_progetto: "Persone in progetto",
    };
    for (const [k, lbl] of Object.entries(req)) {
      const n = toNum(yv[k]);
      if (n === null || n <= 0) return `Anno ${y}: il campo "${lbl}" deve essere maggiore di zero.`;
    }
    if (toNum(yv.persone_progetto) > toNum(yv.dipendenti))
      return "Le persone impegnate nel progetto non possono superare il totale dei dipendenti";
    return null;
  };

  const next = () => {
    const err = validateYear(year, yd);
    if (err) { toast.error(err); return; }
    if (step < 2) setStep(step + 1);
  };
  const prev = () => { if (step > 0) setStep(step - 1); else onBack(); };

  const confirm = () => {
    for (const y of YEARS) {
      const err = validateYear(y, data[y]);
      if (err) { toast.error(err); return; }
    }
    const anyCompiled = YEARS.some((y) => touched(data[y]));
    if (!anyCompiled) { toast.error("È necessario compilare almeno un anno."); return; }
    const anni = YEARS.filter((y) => touched(data[y])).map((y) => ({
      anno: y,
      fatturato: toNum(data[y].fatturato),
      costo_personale: toNum(data[y].costo_personale),
      dipendenti: toNum(data[y].dipendenti),
      persone_progetto: toNum(data[y].persone_progetto),
      costo_amministratore: toNum(data[y].costo_amministratore) || 0,
      costi_professionisti: toNum(data[y].costi_professionisti) || 0,
    }));
    onConfirm(anni, data);
  };

  const pct = ((step + 1) / 3) * 100;

  return (
    <div className="animate-rise" data-testid="year-wizard">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#2B3A42]">
          Anno {year}
        </h2>
        <span className="text-sm text-[#5B6E78]">Step {step + 1} di 3</span>
      </div>
      <Progress data-testid="wizard-progress-bar" value={pct} className="h-2 bg-[#EDF2F0] mb-1" />
      <div className="flex gap-2 mb-7">
        {YEARS.map((y, i) => (
          <span key={y} className={`text-xs font-medium ${i === step ? "text-[#6C8EAD]" : "text-[#5B6E78]/60"}`}>{y}</span>
        ))}
      </div>

      <p className="text-sm text-[#5B6E78] mb-5">
        Puoi lasciare un anno completamente vuoto: verrà escluso dalla stima.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <NumField label="Fatturato (€)" testid="input-fatturato" value={yd.fatturato} onChange={(v) => up("fatturato", v)} />
        <NumField label="Costo del personale dipendente complessivo (€)" testid="input-costo-personale" value={yd.costo_personale} onChange={(v) => up("costo_personale", v)} />
        <NumField label="Numero totale dipendenti" testid="input-dipendenti" value={yd.dipendenti} onChange={(v) => up("dipendenti", v)} integer />
        <NumField label="Persone impegnate in progetti di innovazione" testid="input-persone" value={yd.persone_progetto} onChange={(v) => up("persone_progetto", v)} integer />
        <NumField label="Costo dell'amministratore imputabile al progetto (€)"
          help="Compenso e oneri dell'amministratore che ha partecipato al progetto. Dato rilevato per l'analisi approfondita: non incide sulla stima."
          testid="input-amministratore" value={yd.costo_amministratore} onChange={(v) => up("costo_amministratore", v)} />
        <NumField label="Totale fatturato dai professionisti esterni per il progetto (€)"
          help="Somma delle fatture dei professionisti esterni relative al progetto. Dato rilevato per l'analisi approfondita: non incide sulla stima."
          testid="input-professionisti" value={yd.costi_professionisti} onChange={(v) => up("costi_professionisti", v)} />
      </div>

      <div className="mt-8 flex items-center justify-between gap-3">
        <Button variant="ghost" data-testid="wizard-btn-prev" onClick={prev} className="text-[#5B6E78] hover:text-[#2B3A42]">
          Indietro
        </Button>
        {step < 2 ? (
          <Button data-testid="wizard-btn-next" onClick={next} className="h-11 px-8 rounded-full bg-[#6C8EAD] hover:bg-[#4F708C] text-white">
            Anno successivo
          </Button>
        ) : (
          <Button data-testid="wizard-btn-calculate" onClick={confirm} disabled={submitting}
            className="h-11 px-8 rounded-full bg-[#9CAF88] hover:bg-[#7C8F69] text-white">
            {submitting ? "Calcolo..." : "Calcola stima"}
          </Button>
        )}
      </div>
    </div>
  );
};
